import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  Input,
  Divider,
  Avatar,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  FiUser,
  FiShoppingBag,
  FiShoppingCart,
  FiMapPin,
  FiHelpCircle,
  FiEdit2,
  FiCheck,
  FiX,
  FiLogOut,
  FiShield,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { apiUrl } from "../ApiUrl/ApiUrl";
import { Authcontext } from "../Context/Authcontext";
import { CartContext } from "../Context/CartContext";

const MyProfile = () => {
  const [user, setUser] = useState(null);
  const { logout } = useContext(Authcontext);
  const { clearCart } = useContext(CartContext);
  const [editMode, setEditMode] = useState(false);
  const [newName, setNewName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const token = localStorage.getItem("token");
    const uuid = localStorage.getItem("uid");

    if (!uuid) {
      setLoading(false);
      return;
    }

    fetch(`${apiUrl}/adminuser/getByid?uid=${uuid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setUser(data);
        setNewName(data.name || "");
      })
      .catch((err) => console.error("Error fetching profile:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleEditClick = () => setEditMode(true);
  const handleCancel = () => {
    setEditMode(false);
    setNewName(user ? user.name : "");
  };

  const handleSave = () => {
    if (!newName.trim()) {
      toast({
        title: "Name cannot be empty",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    fetch(`${apiUrl}/adminuser/updatename`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id: user.id, name: newName }),
    })
      .then((res) => {
        if (res.ok) {
          setUser({ ...user, name: newName });
          setEditMode(false);
          localStorage.setItem("loginsetName", newName);
          toast({
            title: "Profile Updated",
            description: "Your name has been successfully updated.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          window.location.reload();
        } else {
          toast({
            title: "Update Failed",
            description: "Unable to update profile name. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      })
      .catch((err) => {
        console.error("Error updating name:", err);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleLogout = () => {
    if (logout) logout();
    if (clearCart) clearCart();
    toast({
      title: "Signed Out Successfully",
      description: "Your session and cart items have been cleared.",
      status: "info",
      duration: 2500,
      position: "top",
    });
    navigate("/");
  };

  if (loading) {
    return (
      <Box minH="80vh" bg="#F8FAFC" py={12}>
        <Center>
          <VStack spacing={4}>
            <Spinner size="xl" color="#162237" thickness="3px" />
            <Text color="gray.600" fontSize="sm">
              Loading your account profile...
            </Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box minH="80vh" bg="#F8FAFC" py={12}>
        <Container maxW="600px" textAlign="center">
          <Box bg="white" p={8} rounded="xl" shadow="xs" border="1px solid" borderColor="gray.200">
            <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={2}>
              Unable to load account
            </Text>
            <Text fontSize="sm" color="gray.500" mb={6}>
              Please verify your login session or sign in again.
            </Text>
            <Link to="/login">
              <Button bg="#162237" color="white" _hover={{ bg: "#0F172A" }}>
                Go to Login
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>
    );
  }

  const roleName = user.role || "USER";

  return (
    <Box minH="85vh" bg="#F8FAFC" py={{ base: 4, md: 8 }}>
      <Container maxW="1000px" px={{ base: 3, md: 6 }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb
          spacing="8px"
          separator={<ChevronRightIcon color="gray.400" />}
          fontSize="xs"
          color="gray.500"
          mb={4}
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/" _hover={{ color: "#162237" }}>
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink fontWeight="bold" color="#162237">
              My Profile
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Profile Header Hero Card */}
        <Box
          bg="white"
          rounded="2xl"
          border="1px solid"
          borderColor="gray.200"
          shadow="xs"
          overflow="hidden"
          mb={6}
        >
          {/* Header Banner */}
          <Box h="110px" bgGradient="linear(to-r, #162237, #1E293B, #0F172A)" position="relative" />

          {/* Profile User Info Row */}
          <Box px={{ base: 4, md: 8 }} pb={{ base: 6, md: 8 }} pt={0} position="relative">
            <Flex
              direction={{ base: "column", sm: "row" }}
              justify="space-between"
              align={{ base: "flex-start", sm: "flex-end" }}
              gap={4}
              mt="-45px"
              mb={6}
            >
              <HStack spacing={4} align="flex-end">
                <Avatar
                  size="2xl"
                  name={user.name || user.username}
                  bg="#162237"
                  color="#EAB308"
                  border="4px solid white"
                  shadow="md"
                />
                <Box mb={2}>
                  <HStack spacing={2} wrap="wrap">
                    <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="extrabold" color="#162237">
                      {user.name || "Legal Professional"}
                    </Text>
                    <Badge
                      colorScheme={roleName === "ADMIN" ? "red" : roleName === "staff" ? "purple" : "blue"}
                      fontSize="xs"
                      px={2}
                      py={0.5}
                      rounded="md"
                      fontWeight="bold"
                    >
                      {roleName}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color="gray.500" mt={0.5}>
                    Member ID: #{user.id || localStorage.getItem("uid")}
                  </Text>
                </Box>
              </HStack>

              <HStack spacing={2} alignSelf={{ base: "stretch", sm: "auto" }}>
                {!editMode && (
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="navy"
                    borderColor="gray.300"
                    leftIcon={<FiEdit2 />}
                    rounded="lg"
                    onClick={handleEditClick}
                  >
                    Edit Profile
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  leftIcon={<FiLogOut />}
                  rounded="lg"
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </HStack>
            </Flex>

            {/* Profile Fields */}
            <Box
              bg="gray.50"
              p={{ base: 4, md: 6 }}
              rounded="xl"
              border="1px solid"
              borderColor="gray.200"
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Full Name */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    Full Name
                  </Text>
                  {editMode ? (
                    <HStack mt={2}>
                      <Input
                        size="sm"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        bg="white"
                        borderColor="gray.300"
                        rounded="md"
                        _focus={{ borderColor: "#162237" }}
                        autoFocus
                      />
                      <Button
                        size="sm"
                        bg="#162237"
                        color="white"
                        _hover={{ bg: "#0F172A" }}
                        leftIcon={<FiCheck />}
                        isLoading={isSaving}
                        onClick={handleSave}
                        rounded="md"
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiX />}
                        onClick={handleCancel}
                        rounded="md"
                      >
                        Cancel
                      </Button>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" fontWeight="bold" color="gray.800" mt={1}>
                      {user.name || "Not specified"}
                    </Text>
                  )}
                </Box>

                {/* Email / Username */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    Registered Email / Username
                  </Text>
                  <HStack mt={1} spacing={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                      {user.username}
                    </Text>
                    <Badge colorScheme="green" fontSize="10px" px={1.5} rounded="sm">
                      Verified
                    </Badge>
                  </HStack>
                </Box>

                {/* Account Security */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    Account Security
                  </Text>
                  <HStack spacing={2} mt={1} color="gray.700" fontSize="sm">
                    <Icon as={FiShield} color="green.600" />
                    <Text fontSize="xs" color="gray.600">
                      Password protected & session encrypted
                    </Text>
                  </HStack>
                </Box>

                {/* Account Type */}
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">
                    Privilege Level
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800" mt={1}>
                    {roleName === "ADMIN"
                      ? "Store Administrator"
                      : roleName === "staff"
                      ? "Editorial & Order Staff"
                      : "Verified Customer"}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>
          </Box>
        </Box>

        {/* Quick Access Account Links */}
        <Text fontSize="sm" fontWeight="bold" color="gray.600" textTransform="uppercase" letterSpacing="wider" mb={3} px={1}>
          Quick Account Navigation
        </Text>

        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
          {/* Link 1: Orders */}
          <Link to="/orderpage">
            <Box
              bg="white"
              p={5}
              rounded="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="xs"
              _hover={{ borderColor: "#162237", shadow: "sm", transform: "translateY(-2px)" }}
              transition="all 0.2s"
              cursor="pointer"
              h="100%"
            >
              <Flex
                w="42px"
                h="42px"
                rounded="lg"
                bg="blue.50"
                color="#162237"
                align="center"
                justify="center"
                fontSize="20px"
                mb={3}
              >
                <FiShoppingBag />
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                My Orders
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Track shipments, view purchases & download invoices.
              </Text>
            </Box>
          </Link>

          {/* Link 2: Cart */}
          <Link to="/cart">
            <Box
              bg="white"
              p={5}
              rounded="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="xs"
              _hover={{ borderColor: "#162237", shadow: "sm", transform: "translateY(-2px)" }}
              transition="all 0.2s"
              cursor="pointer"
              h="100%"
            >
              <Flex
                w="42px"
                h="42px"
                rounded="lg"
                bg="orange.50"
                color="#fb641b"
                align="center"
                justify="center"
                fontSize="20px"
                mb={3}
              >
                <FiShoppingCart />
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                Shopping Cart
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Review items saved in your cart and proceed to checkout.
              </Text>
            </Box>
          </Link>

          {/* Link 3: Customer Care */}
          <Link to="/contact">
            <Box
              bg="white"
              p={5}
              rounded="xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="xs"
              _hover={{ borderColor: "#162237", shadow: "sm", transform: "translateY(-2px)" }}
              transition="all 0.2s"
              cursor="pointer"
              h="100%"
            >
              <Flex
                w="42px"
                h="42px"
                rounded="lg"
                bg="green.50"
                color="green.700"
                align="center"
                justify="center"
                fontSize="20px"
                mb={3}
              >
                <FiHelpCircle />
              </Flex>
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                Customer Support
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Have queries regarding orders, bare acts or subscriptions? Reach out.
              </Text>
            </Box>
          </Link>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default MyProfile;

